import * as z from "zod/v4";

export interface StreamChannelSchemaDefinition<TInput, TInitial, TEvent> {
  readonly inputSchema: z.ZodType<TInput>;
  readonly initialSchema: z.ZodType<TInitial>;
  readonly eventSchema: z.ZodType<TEvent>;
}

export function defineStreamChannelSchema<TInput, TInitial, TEvent>(
  definition: StreamChannelSchemaDefinition<TInput, TInitial, TEvent>,
): StreamChannelSchemaDefinition<TInput, TInitial, TEvent> {
  return definition;
}

export type AnyStreamChannelSchemaDefinition = StreamChannelSchemaDefinition<
  unknown,
  unknown,
  unknown
>;

export const streamSessionIdSchema = z.string();
export type StreamSessionId = z.infer<typeof streamSessionIdSchema>;

export const streamSubscriptionKeySchema = z.string();
export type StreamSubscriptionKey = z.infer<typeof streamSubscriptionKeySchema>;

export const streamSessionPathSchema = z.object({
  sessionId: streamSessionIdSchema,
});
export type StreamSessionPath = z.infer<typeof streamSessionPathSchema>;

export const openStreamSessionResponseSchema = z.object({
  sessionId: streamSessionIdSchema,
});
export type OpenStreamSessionResponse = z.infer<
  typeof openStreamSessionResponseSchema
>;

export const streamControlSuccessSchema = z.object({
  success: z.literal(true),
});
export type StreamControlSuccess = z.infer<typeof streamControlSuccessSchema>;

export const teamNotificationsStreamInputSchema = z.object({
  visibility: z.enum(["public", "internal"]).default("internal"),
});
export type TeamNotificationsStreamInput = z.infer<
  typeof teamNotificationsStreamInputSchema
>;

export const userNotificationsStreamInputSchema = z.object({});
export type UserNotificationsStreamInput = z.infer<
  typeof userNotificationsStreamInputSchema
>;

export const exampleStreamInitialSchema = z.object({
  _tag: z.literal("exampleInitial"),
  items: z.array(z.string()),
  scope: z.string(),
});
export type ExampleStreamInitial = z.infer<typeof exampleStreamInitialSchema>;

export const exampleStreamEventSchema = z.object({
  _tag: z.literal("exampleUpdated"),
  item: z.string(),
  scope: z.string(),
});
export type ExampleStreamEvent = z.infer<typeof exampleStreamEventSchema>;

export const exampleStreamInputSchema = z.object({
  scope: z.string().default("default"),
});
export type ExampleStreamInput = z.infer<typeof exampleStreamInputSchema>;

export const exampleStreamChannelSchema = defineStreamChannelSchema({
  eventSchema: exampleStreamEventSchema,
  initialSchema: exampleStreamInitialSchema,
  inputSchema: exampleStreamInputSchema,
});

export const teamNotificationsStreamChannelSchema = defineStreamChannelSchema({
  eventSchema: z.unknown(),
  initialSchema: z.unknown(),
  inputSchema: teamNotificationsStreamInputSchema,
});

export const userNotificationsStreamChannelSchema = defineStreamChannelSchema({
  eventSchema: z.unknown(),
  initialSchema: z.unknown(),
  inputSchema: userNotificationsStreamInputSchema,
});

export const streamChannelSchemaRegistry = {
  example: exampleStreamChannelSchema,
  "team-notifications": teamNotificationsStreamChannelSchema,
  "user-notifications": userNotificationsStreamChannelSchema,
} as const;

export type RegisteredStreamChannelName =
  keyof typeof streamChannelSchemaRegistry;

export type StreamChannelSchemaFor<
  TChannel extends RegisteredStreamChannelName,
> = (typeof streamChannelSchemaRegistry)[TChannel];

const registeredChannelNames = Object.keys(streamChannelSchemaRegistry) as [
  RegisteredStreamChannelName,
  ...RegisteredStreamChannelName[],
];

export const streamChannelNameSchema = z.enum(registeredChannelNames);
export type StreamChannelName = z.infer<typeof streamChannelNameSchema>;

export const streamSubscribeControlInputSchema = z.object({
  action: z.literal("subscribe"),
  channel: streamChannelNameSchema,
  input: z.unknown().optional(),
  subscriptionKey: streamSubscriptionKeySchema,
});
export type StreamSubscribeControlInput = z.infer<
  typeof streamSubscribeControlInputSchema
>;

export const streamUnsubscribeControlInputSchema = z.object({
  action: z.literal("unsubscribe"),
  subscriptionKey: streamSubscriptionKeySchema,
});
export type StreamUnsubscribeControlInput = z.infer<
  typeof streamUnsubscribeControlInputSchema
>;

export const streamControlInputSchema = z.discriminatedUnion("action", [
  streamSubscribeControlInputSchema,
  streamUnsubscribeControlInputSchema,
]);
export type StreamControlInput = z.infer<typeof streamControlInputSchema>;

export const streamSubscribedMessageSchema = z.object({
  _tag: z.literal("subscribed"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
});
export type StreamSubscribedMessage = z.infer<
  typeof streamSubscribedMessageSchema
>;

export const streamInitialMessageSchema = z.object({
  _tag: z.literal("initial"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
  payload: z.unknown(),
});
export type StreamInitialMessage = z.infer<typeof streamInitialMessageSchema>;

export const streamEventMessageSchema = z.object({
  _tag: z.literal("event"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
  payload: z.unknown(),
});
export type StreamEventMessage = z.infer<typeof streamEventMessageSchema>;

export const streamUnsubscribedMessageSchema = z.object({
  _tag: z.literal("unsubscribed"),
  subscriptionKey: streamSubscriptionKeySchema,
  channel: streamChannelNameSchema,
});
export type StreamUnsubscribedMessage = z.infer<
  typeof streamUnsubscribedMessageSchema
>;

export const streamErrorMessageSchema = z.object({
  _tag: z.literal("error"),
  subscriptionKey: streamSubscriptionKeySchema.optional(),
  channel: streamChannelNameSchema.optional(),
  message: z.string(),
});
export type StreamErrorMessage = z.infer<typeof streamErrorMessageSchema>;

export const streamServerMessageSchema = z.discriminatedUnion("_tag", [
  streamSubscribedMessageSchema,
  streamInitialMessageSchema,
  streamEventMessageSchema,
  streamUnsubscribedMessageSchema,
  streamErrorMessageSchema,
]);
export type StreamServerMessage = z.infer<typeof streamServerMessageSchema>;
